import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import Modal from './Modal'
import useAuth from '../models/useAuth'
import { claimCode, routerBaseUrl } from '../lib/cliCode'
import { getOrigin } from '../lib/origin'
import { workosOrganizationId } from '../lib/auth'
import { routerLoginURL } from '../utilities/routerLogin'
import { CODE_LENGTH, afterCode, normalizeCode, typingCode, type CliKind, type CodeFailure } from '../utilities/cliCode'

const TEXT: Record<CliKind, { title: string; lead: string }> = {
  login: { title: 'Sign in a terminal', lead: 'Enter the code that nobgp login shows in the terminal.' },
  register: { title: 'Register a machine', lead: 'Enter the code that nobgp register shows in the terminal.' },
}

/**
 * `/activate`, `/activate/:code`, `/register` and `/register/:code` — the code step of a CLI browser session
 * (nobgp-router#1859). ONE page for both kinds (Benoit, 2026-09-27); each goes on to the page it
 * already had (`utilities/cliCode.ts` `afterCode`).
 *
 * ⚠ The reader signs in FIRST: the router's lookup takes a user bearer, so every guess belongs to
 * an account. A reader with no session goes to `/login` and comes back here, code and all.
 *
 * A code in the URL (the QR code, or the browser the terminal opened) is claimed at once, with no
 * form (Benoit, 2026-09-28). The reader approves on the next page, which shows what they approve:
 * the router's confirmation and the approval form show the machine. A Continue here
 * added a click and no check. A code the router refuses shows the form, with the reason.
 */
export default function CliCode({ kind }: Readonly<{ kind: CliKind }>) {
  const location = useLocation()
  const navigate = useNavigate()
  const { code: fromUrl } = useParams()
  const initialized = useAuth(state => state.initialized)
  const session = useAuth(state => state.session)

  const [code, setCode] = useState(() => typingCode(fromUrl ?? ''))
  // ⚠ A ref, not state: StrictMode runs the effect twice, and a code is good for ONE use. A second
  // claim would get "unknown or expired" for a code that just worked.
  const claimFromUrl = useRef(!!normalizeCode(fromUrl))
  const [sending, setSending] = useState(claimFromUrl.current)
  // True until the code from the URL fails: until then the page shows a spinner and no form.
  const [quiet, setQuiet] = useState(claimFromUrl.current)
  const [failure, setFailure] = useState<CodeFailure | null>(null)

  const claim = async () => {
    setSending(true)
    setFailure(null)
    const result = await claimCode(code, kind)
    if (!result.ok) {
      setSending(false)
      setQuiet(false)
      setFailure(result.failure)
      return
    }
    const next = afterCode(kind, result.session, routerBaseUrl)
    // `replace`: the back button must not return to a code that is now used.
    if ('href' in next) window.location.replace(next.href)
    else navigate(next.path, { replace: true })
  }

  useEffect(() => {
    if (!initialized || !session || !claimFromUrl.current) return
    claimFromUrl.current = false
    void claim() // once, when the reader is signed in
  }, [initialized, session])

  if (!initialized) return null
  // ⚠ Not on a 401 from the router: /login would see the app's session and send the reader
  // straight back, a loop. That failure is said beside the field instead.
  if (!session) return <Navigate to="/login" replace state={{ from: location }} />

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!normalizeCode(code)) {
      setFailure({ kind: 'miss', message: `Enter the ${CODE_LENGTH} characters of the code.` })
      return
    }
    await claim()
  }

  const signInAgain = () => {
    const origin = getOrigin()
    if (!origin) return
    const next = new URLSearchParams({ next: `${location.pathname}${location.search}` })
    window.location.assign(
      routerLoginURL(routerBaseUrl, {
        returnTo: `${origin}/auth/callback?${next.toString()}`,
        organizationId: workosOrganizationId || undefined,
      })
    )
  }

  // A code from the URL: a spinner and nothing else until the next page. The card's title flashed
  // for a moment and read as a step (Benoit, 2026-09-28).
  if (quiet)
    return (
      <Stack sx={{ justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: '90vh' }}>
        <CircularProgress size={28} aria-label="Checking the code" />
      </Stack>
    )

  return (
    <Stack sx={{ justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: '90vh', px: 2 }}>
      <Modal open inline maxWidth="xs" color="primary.main" disableOverflow sx={{ maxHeight: 'none' }}>
        <DialogContent>
          <Stack component="form" onSubmit={submit} sx={{ gap: 3, py: 1 }}>
            <Typography variant="subtitle1" color="primary">
              {TEXT[kind].title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {TEXT[kind].lead}
            </Typography>
            <TextField
              value={code}
              onChange={e => {
                setCode(typingCode(e.target.value))
                setFailure(null)
              }}
              autoFocus={!fromUrl || !!failure}
              autoComplete="one-time-code"
              placeholder="····"
              error={!!failure}
              helperText={failure?.message}
              slotProps={{
                htmlInput: {
                  'aria-label': 'Code',
                  maxLength: CODE_LENGTH + 2,
                  autoCapitalize: 'characters',
                  spellCheck: false,
                  style: { fontFamily: 'monospace', fontSize: 28, letterSpacing: '0.4em', textAlign: 'center' },
                },
              }}
            />
            <Button type="submit" variant="contained" color="primary" size="large" disabled={sending}>
              {sending ? <CircularProgress color="inherit" size={20} /> : 'Continue'}
            </Button>
            {/* ⚠ A token the router refuses even after a refresh: sign in again at the ROUTER and
                come back here. Not /login, which would see the app's session and send the reader
                straight back to this page. */}
            {failure?.kind === 'unauthenticated' && (
              <Button variant="outlined" color="primary" onClick={signInAgain}>
                Sign in again
              </Button>
            )}
          </Stack>
        </DialogContent>
      </Modal>
    </Stack>
  )
}
